import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Bookmark, Pencil, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

function preview(text, n = 90) {
  const t = (text || "").trim().replace(/\s+/g, " ");
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

/**
 * Saved Query-Data prompts. Users save prompts, click one to FILL the input
 * (they can then edit/send), and can edit or delete saved prompts. Prompts are
 * per-user and persistent. Exposes an imperative `savePrompt(text)` so the input
 * bar's "Save" button can save the current draft.
 */
const SavedPrompts = forwardRef(function SavedPrompts({ onFill }, ref) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, label, text} or null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listSavedPrompts();
      setPrompts(res.prompts || []);
    } catch (e) {
      logError("Failed to load saved prompts:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Open the editor pre-filled to save a NEW prompt from the current input draft.
  const openSaveNew = useCallback((text) => {
    setEditing({ id: null, label: "", text: (text || "").trim() });
  }, []);

  useImperativeHandle(ref, () => ({ savePrompt: openSaveNew }), [openSaveNew]);

  const submitEditor = async () => {
    const text = (editing.text || "").trim();
    if (!text) {
      toast.error("Prompt text cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const res = await api.updateSavedPrompt(editing.id, { label: editing.label, text });
        setPrompts((prev) => prev.map((p) => (p.id === res.prompt.id ? res.prompt : p)));
        toast.success("Prompt updated.");
      } else {
        const res = await api.createSavedPrompt(text, editing.label);
        setPrompts((prev) => [res.prompt, ...prev]);
        toast.success("Prompt saved.");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save prompt.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete saved prompt "${p.label}"?`)) return;
    try {
      await api.deleteSavedPrompt(p.id);
      setPrompts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Prompt deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete prompt.");
    }
  };

  return (
    <div className="mb-4" data-testid="saved-prompts">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <Bookmark className="w-4 h-4 text-primary" /> Saved prompts
        </p>
        <Button
          size="sm"
          variant="outline"
          data-testid="saved-prompts-new"
          onClick={() => setEditing({ id: null, label: "", text: "" })}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : prompts.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="saved-prompts-empty">
          No saved prompts yet. Type a prompt below and click <span className="font-medium">Save</span>, or use{" "}
          <span className="font-medium">New</span>.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5" data-testid="saved-prompts-list">
          {prompts.map((p) => (
            <div
              key={p.id}
              data-testid="saved-prompt-item"
              className="group flex items-center gap-2 rounded-lg border border-border px-3 py-2 hover:border-primary/40 transition-colors"
            >
              <button
                type="button"
                data-testid="saved-prompt-fill"
                onClick={() => onFill?.(p.text)}
                className="flex-1 min-w-0 text-left"
                title="Click to fill the prompt box"
              >
                <span className="block text-sm font-medium text-foreground truncate">{p.label}</span>
                <span className="block text-xs text-muted-foreground truncate">{preview(p.text)}</span>
              </button>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  data-testid="saved-prompt-edit"
                  onClick={() => setEditing({ id: p.id, label: p.label, text: p.text })}
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-red-600 hover:text-red-600"
                  data-testid="saved-prompt-delete"
                  onClick={() => remove(p)}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="saved-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit saved prompt" : "Save prompt"}</DialogTitle>
            <DialogDescription>
              Give it a short label and the prompt text. Saved prompts are private to your account.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Label</label>
                <Input
                  data-testid="saved-prompt-label-input"
                  value={editing.label}
                  onChange={(e) => setEditing((s) => ({ ...s, label: e.target.value }))}
                  placeholder="e.g. Weekly forecast check"
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Prompt</label>
                <Textarea
                  data-testid="saved-prompt-text-input"
                  value={editing.text}
                  onChange={(e) => setEditing((s) => ({ ...s, text: e.target.value }))}
                  placeholder="The prompt you want to save…"
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submitEditor} disabled={saving} data-testid="saved-prompt-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing?.id ? "Save changes" : "Save prompt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default SavedPrompts;
